import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useActiveMission, useCanvases, useCurrentProject, useProjects, useWorkspaceStore } from '@shared/state';
import type { MissionCanvas } from '@shared/types';

type MissionStatus = 'draft' | 'cadrage' | 'active' | 'closed';

const MISSION_STATUS_LABELS: Record<MissionStatus, string> = {
  draft: 'Brouillon',
  cadrage: 'Cadrage',
  active: 'Active',
  closed: 'Clôturée',
};

function getMissionStatus(project: { missionStatus?: MissionStatus }) {
  return project.missionStatus || 'draft';
}

export function MissionSelector() {
  const navigate = useNavigate();
  const projects = useProjects();
  const canvases = useCanvases();
  const activeMission = useActiveMission();
  const currentProject = useCurrentProject();
  const setActiveMission = useWorkspaceStore((state) => state.setActiveMission);
  const [open, setOpen] = useState(false);
  const rootRef = useRef<HTMLDivElement>(null);

  const visibleProjects = useMemo(
    () => projects.filter((project) => !project.archivedAt),
    [projects],
  );

  const resolvedMission = activeMission || currentProject || visibleProjects[0] || null;
  const canvasByProjectId = useMemo(
    () => new Map(canvases.map((canvas) => [canvas.projectId, canvas] as const)),
    [canvases],
  );
  const verdictLabels: Record<NonNullable<MissionCanvas['scoreVerdict']>, string> = {
    flou: 'Flou',
    cadrable: 'Cadrable',
    solide: 'Solide',
    pret: 'Prêt',
  };

  useEffect(() => {
    function handlePointerDown(event: MouseEvent) {
      if (!rootRef.current?.contains(event.target as Node)) {
        setOpen(false);
      }
    }

    function handleKeyDown(event: KeyboardEvent) {
      if (event.key === 'Escape') setOpen(false);
    }

    document.addEventListener('mousedown', handlePointerDown);
    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('mousedown', handlePointerDown);
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, []);

  function handleMissionSelect(projectId: string) {
    setActiveMission(projectId);
    setOpen(false);
  }

  function handleNewMission() {
    setOpen(false);
    navigate('/missions/new');
  }

  const styles = (
    <style>{`
      .mission-selector {
        position: relative;
        min-width: 0;
        width: 100%;
      }

      .mission-selector-empty {
        width: fit-content;
        min-width: 0;
        appearance: none;
        -webkit-appearance: none;
        display: flex;
        align-items: center;
        justify-content: flex-start;
        min-height: 44px;
        border: 0;
        background: linear-gradient(180deg, #ff6b3d 0%, #eb3000 100%) !important;
        color: #ffffff !important;
        border-radius: 12px;
        padding: 12px 14px;
        font-size: 14px;
        line-height: 1.25;
        text-align: left;
        cursor: pointer;
        box-sizing: border-box;
        transition: opacity 0.15s, transform 0.15s;
        box-shadow: 0 10px 24px rgba(235, 48, 0, 0.22);
      }

      @media (max-width: 767px) {
        .mission-selector-empty {
          width: 100%;
        }
      }

      .mission-selector-empty:hover {
        opacity: 0.92;
        transform: translateY(-1px);
      }

      .mission-selector-trigger {
        width: 100%;
        min-width: 0;
        display: flex;
        align-items: stretch;
        gap: 10px;
        min-height: 44px;
        border: 1px solid var(--ix-border-subtle);
        border-radius: 12px;
        background: var(--ix-bg-surface) !important;
        color: var(--ix-text-primary);
        padding: 12px 14px;
        cursor: pointer;
        text-align: left;
        box-shadow: inset 0 1px 0 rgba(255, 255, 255, 0.02);
        box-sizing: border-box;
        transition: border-color 0.15s, background 0.15s, color 0.15s;
      }

      .mission-selector-trigger:hover {
        border-color: color-mix(in srgb, var(--ix-accent) 18%, var(--ix-border-subtle));
        background: var(--ix-bg-elevated);
      }

      .mission-selector-trigger-left {
        flex: 1;
        min-width: 0;
        display: flex;
        flex-direction: column;
      }

      .mission-selector-trigger-right {
        flex: 0 0 auto;
        display: flex;
        flex-direction: column;
        align-items: center;
        justify-content: flex-end;
        gap: 2px;
      }

      .mission-selector-label {
        flex: 0 0 auto;
        font-size: 11px;
        letter-spacing: 0.08em;
        text-transform: uppercase;
        color: var(--ix-text-tertiary);
      }

      .mission-selector-value {
        flex: 1;
        min-width: 0;
        overflow: hidden;
        text-overflow: ellipsis;
        white-space: nowrap;
        font-size: 14px;
        font-weight: 600;
        color: var(--ix-text-primary);
      }

      .mission-selector-initial {
        width: 20px;
        height: 20px;
        display: flex;
        align-items: center;
        justify-content: center;
        font-size: 11px;
        font-weight: 700;
        border-radius: 50%;
        line-height: 1;
      }

      .mission-selector-caret {
        flex: 0 0 auto;
        color: var(--ix-text-tertiary);
        font-size: 11px;
      }

      .mission-selector-menu {
        position: absolute;
        top: calc(100% + 8px);
        left: 0;
        right: 0;
        z-index: 9999;
        background: var(--ix-bg-elevated);
        border: 1px solid var(--ix-border-subtle);
        border-radius: 14px;
        box-shadow: 0 16px 40px rgba(0, 0, 0, 0.28);
        overflow: hidden;
      }

      .mission-selector-list {
        max-height: 280px;
        overflow: auto;
        padding: 6px;
      }

      .mission-selector-item {
        width: 100%;
        display: flex;
        align-items: center;
        justify-content: space-between;
        gap: 8px;
        min-height: 44px;
        padding: 12px 14px;
        border: 0;
        border-radius: 10px;
        background: transparent;
        color: var(--ix-text-primary);
        cursor: pointer;
        text-align: left;
      }

      .mission-selector-item:hover,
      .mission-selector-item.active {
        background: var(--ix-accent-bg);
      }

      .mission-selector-item .mission-selector-initial {
        margin-left: auto;
        align-self: flex-end;
      }

      .mission-selector-item-main {
        min-width: 0;
        display: flex;
        flex-direction: column;
        gap: 2px;
      }

      .mission-selector-item-title {
        font-size: 14px;
        font-weight: 700;
        line-height: 1.25;
      }

      .mission-selector-item-client {
        font-size: 11px;
        line-height: 1.35;
        color: var(--ix-text-tertiary);
      }

      .mission-selector-score {
        flex: 0 0 auto;
        font-size: 11px;
        padding: 3px 8px;
        border-radius: 999px;
        background: var(--ix-accent-bg);
        color: var(--ix-text-primary);
        white-space: nowrap;
      }

      .mission-status-active {
        color: var(--ix-positive);
      }
      .mission-status-active.mission-selector-initial {
        background: color-mix(in srgb, var(--ix-positive) 20%, transparent);
      }

      .mission-status-cadrage {
        color: var(--ix-warning);
      }
      .mission-status-cadrage.mission-selector-initial {
        background: color-mix(in srgb, var(--ix-warning) 20%, transparent);
      }

      .mission-status-closed {
        color: var(--ix-text-tertiary);
      }
      .mission-status-closed.mission-selector-initial {
        background: var(--ix-bg-surface);
      }

      .mission-status-draft {
        color: var(--ix-text-tertiary);
      }
      .mission-status-draft.mission-selector-initial {
        background: var(--ix-bg-surface);
      }

      .mission-selector-new {
        width: 100%;
        min-height: 44px;
        border: 0;
        border-top: 1px solid var(--ix-border-subtle);
        background: var(--ix-bg-surface) !important;
        color: var(--ix-text-primary);
        font-size: 14px;
        font-weight: 600;
        padding: 12px 14px;
        cursor: pointer;
      }

      .mission-selector-new:hover {
        color: var(--ix-accent);
      }
    `}</style>
  );

  if (visibleProjects.length === 0) {
    return (
      <>
        {styles}
        <button type="button" className="mission-selector-empty" onClick={handleNewMission}>
          Créer une mission
        </button>
      </>
    );
  }

  return (
    <div ref={rootRef} className="mission-selector">
      {styles}
      <button
        type="button"
        className="mission-selector-trigger"
        aria-haspopup="menu"
        aria-expanded={open}
        onClick={() => setOpen((value) => !value)}
      >
        <span className="mission-selector-trigger-left">
          <span className="mission-selector-label">Projet</span>
          <span className="mission-selector-value">
            {resolvedMission ? resolvedMission.name : 'Aucune mission'}
          </span>
        </span>
        <span className="mission-selector-trigger-right">
          <span className="mission-selector-caret">▾</span>
        </span>
      </button>

      {open && (
        <div className="mission-selector-menu" role="menu" aria-label="Sélecteur de mission">
          <div className="mission-selector-list">
            {visibleProjects.map((project) => {
              const isSelected = resolvedMission?.id === project.id;
              const status = getMissionStatus(project);
              const projectCanvas = canvasByProjectId.get(project.id);
              return (
                <button
                  key={project.id}
                  type="button"
                  className={`mission-selector-item ${isSelected ? 'active' : ''}`}
                  onClick={() => handleMissionSelect(project.id)}
                  role="menuitemradio"
                  aria-checked={isSelected}
                >
                  <span className="mission-selector-item-main">
                    <span className="mission-selector-item-title">{project.name}</span>
                    <span className="mission-selector-item-client">{project.client || 'Sans client'}</span>
                  </span>
                  {projectCanvas && (
                    <span className="mission-selector-score">
                      {projectCanvas.score}/100 · {verdictLabels[projectCanvas.scoreVerdict]}
                    </span>
                  )}
                  <span className={`mission-selector-initial mission-status-${status}`}>
                    {MISSION_STATUS_LABELS[status].charAt(0)}
                  </span>
                </button>
              );
            })}
          </div>

          <button type="button" className="mission-selector-new" onClick={handleNewMission}>
            + Nouvelle mission
          </button>
        </div>
      )}

    </div>
  );
}
